import React, { useRef, useState } from "react";
import { Button, CircularProgress, Typography } from "@material-ui/core";
import { datadogLogs } from "@datadog/browser-logs";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import MainContainer from "components/Layout/MainContainer";
import useStyles from "./styles";
import { useAnalyticsLog } from "app/utils/hooks/useAnalyticsLog";
import Footer from "../Layout/Footer";
import Menu from "../Layout/Menu";
import useChangeTitle from "app/utils/hooks/useChangeTitle";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { getPartnerToken } from "../../app/utils/AuthRemote";
import generalSettings from "../../app/utils/settings";
import { events } from "../../app/utils/analytics/events";
import { useSelector } from "react-redux";
import { selectPartnerSettings } from "app/redux/selectors/conversationSelector";
import AcceptTerms from "./AcceptTerms";

function NewConversation() {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const classes = useStyles();
  const navigate = useNavigate();
  useAnalyticsLog(events.navigation.introNew);
  const title = t({
    id: "app.title.get-token",
    message:
      "Connect with a Crisis Text Line crisis counselor via an online text conversation",
  });
  const disconnectDisclaimerText = t({
    id: "app.get-token.disconnect-disclaimer-text",
    message:
      "Please note that while you chat with us you must remain on the website. If you navigate away from the chat or go to another app your connection may be lost.",
  });

  useChangeTitle(title);
  const partnerSettings = useSelector(selectPartnerSettings);

  const handleStartConversation = async () => {
    setLoading(true);

    let recaptchaToken = null;

    if (generalSettings.recaptchaPublicKey) {
      if (!recaptchaRef.current) {
        datadogLogs.logger.error("Recaptcha not loaded", {
          env: process.env.NODE_ENV,
        });
        setError("Recaptcha not loaded");
        setLoading(false);
        return;
      }

      const recaptcha = recaptchaRef.current;
      const recaptchaPrevToken = recaptcha.getValue();
      if (recaptchaPrevToken !== "") {
        recaptcha.reset();
      }
      recaptchaToken = await recaptcha.executeAsync();
      if (!recaptchaToken) {
        setLoading(false);
        return;
      }
    }

    getPartnerToken(recaptchaToken || "")
      .then((obj) => {
        // redirect to auth route with the token.
        const token = obj.url.substring(obj.url.lastIndexOf("/") + 1);
        navigate(`/${token}`);
      })
      .catch((e) => {
        datadogLogs.logger.error(
          e.message,
          {
            env: process.env.NODE_ENV,
          },
          e
        );
        setError(e.message);
        setLoading(false);
      });
  };

  // NOTE: `partnerSettings.text.ui.disconnectDisclaimer` is used only as a feature flag.
  // Its string content is ignored: the actual disclaimer text always comes from the
  // i18n translation `disconnectDisclaimerText` defined above.
  const showDisconnectDisclaimer =
    partnerSettings.text.ui.disconnectDisclaimer !== "";

  return (
    <>
      <MainContainer header type={"intro"} containerShowMenu={false}>
        <section className={classes.about} style={{ justifyContent: "center" }}>
          <div className={classes.newConvoInfo}>
            <div>
              <Typography variant="h1" className={classes.welcomeText}>
                {/* 
                  Trans does not have proper TypeScript types because of React typing bug. Upgrading React may resolve this. 
                  https://stackoverflow.com/a/71848896
                */}
                {/* @ts-ignore */}
                <Trans id="welcome-text">
                  You're not alone. Connect with a trained, volunteer crisis
                  responder.
                </Trans>
              </Typography>
            </div>
            <div
              style={{
                paddingRight: "4px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartConversation}
              >
                {loading && <CircularProgress size={26} color="secondary" />}
                {/* @ts-ignore */}
                {!loading && <Trans id="lets-chat-button">Let's Chat</Trans>}
              </Button>
              {error && (
                <Typography color={"error"} style={{ padding: "8px" }}>
                  {error}
                </Typography>
              )}
              <AcceptTerms />
              {showDisconnectDisclaimer && (
                <Typography className={classes.disconnectDisclaimerText}>
                  {disconnectDisclaimerText}
                </Typography>
              )}
              {/* 
                ReCAPTCHA does not have proper TypeScript types because of React typing bug. Upgrading React may resolve this. 
                https://stackoverflow.com/a/71848896
              */}
              {generalSettings.recaptchaPublicKey ? (
                /* @ts-ignore */
                <ReCAPTCHA
                  ref={recaptchaRef}
                  className={classes.recaptchaContainer}
                  sitekey={generalSettings.recaptchaPublicKey}
                  size="invisible"
                />
              ) : null}
            </div>
          </div>
        </section>
        <Menu />
        <Footer />
      </MainContainer>
    </>
  );
}

export default NewConversation;
